import {
  Ellipsis,
  NewButton,
  NewButtonSize,
  NewButtonVariant,
  SlidingPanel,
} from "@visiblethread/react-components";
import Flex, { ALIGN_CLASS, DIRECTION_CLASS, GAP_CLASS } from "components/Flex";
import SplitVerticalPanels from "components/SplitVerticalPanels";
import { ExternalLink, XIcon } from "lucide-react";
import { func, number, oneOfType, string } from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { buildCoverageRows } from "slices/proposalOutline/coverage";
import {
  selectCoverage,
  selectPrimaryOutlineDocumentId,
  selectRequirements,
} from "slices/proposalOutline/selectors";

import {
  COVERAGE_DOCUMENT_MAX_WIDTH,
  COVERAGE_DOCUMENT_MIN_WIDTH,
  COVERAGE_PANE_NARROW_WIDTH,
  DRAWER_DOCUMENT_WIDTH,
  DRAWER_REQUIREMENTS_MIN_WIDTH,
} from "../../constants";
import CoverageRequirementsTable from "../CoverageRequirementsTable";
import TraceDocumentPane from "./TraceDocumentPane";
import styles from "./TraceDrawer.module.scss";

const TITLE_ID = "trace-drawer-title";

// Tracing a section answers "what must THIS section address", so the drawer lists
// that section's requirements beside the source document they were read out of,
// without leaving the outline. Deliberately reports only a count: every row here is
// covered by construction (getVisibleRequiredPoints filters to the section's own
// coveredPointIds), so the Coverage tab's covered/gap arithmetic would print two
// constants dressed up as measurements.
const TraceDrawer = ({ sectionId, onClose, onOpenInCoverage }) => {
  const coverage = useSelector(selectCoverage);
  const requirements = useSelector(selectRequirements);
  const primaryDocumentId = useSelector(selectPrimaryOutlineDocumentId);

  const [activeRowId, setActiveRowId] = useState(null);
  const [isDocumentNarrow, setIsDocumentNarrow] = useState(false);
  const [documentWidth, setDocumentWidth] = useState(DRAWER_DOCUMENT_WIDTH);
  const panelRef = useRef(null);

  useEffect(() => {
    setActiveRowId(null);
  }, [sectionId]);

  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(() => {
    const onMouseDown = (event) => {
      const { target } = event;
      // VerificationCell's quote popover is portalled (the component library renders it with
      // role="tooltip"), so in the DOM it sits outside panelRef even though it belongs to the
      // drawer. Without this, clicking a quote to read the source text dismissed the whole drawer.
      if (target instanceof Element && target.closest('[role="tooltip"]')) {
        return;
      }
      if (panelRef.current && !panelRef.current.contains(target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [onClose]);

  const activeSection = useMemo(
    () => coverage.sections.find((section) => section.id === sectionId) || null,
    [coverage.sections, sectionId]
  );

  const rows = useMemo(
    () => buildCoverageRows({ coverage, requirements, activeSection }),
    [coverage, requirements, activeSection]
  );

  const activeRowIndex = rows.findIndex((row) => row.id === activeRowId);
  const activeRow = rows[activeRowIndex] || null;
  const activeRequirement = activeRow?.requirement || null;

  // OutlineTable owns drawerSectionId and keeps it set regardless of what renders here, so
  // returning null on an unresolvable section left the drawer "open" but invisible: nothing to
  // click, Escape bound to a component the user can no longer see, and focus never returned to
  // traceTriggerRef. Tell the parent to close instead of just disappearing.
  useEffect(() => {
    if (!activeSection) {
      onClose();
    }
  }, [activeSection, onClose]);

  if (!activeSection) {
    return null;
  }

  const documentId = activeRequirement?.sourceDocumentId ?? primaryDocumentId;

  // Pinned at both ends while collapsed so the resizer cannot drag a narrowed pane back
  // open — the viewer toolbar's toggle stays the only way in and out of the narrow state.
  const documentPanel = {
    size: isDocumentNarrow ? COVERAGE_PANE_NARROW_WIDTH : documentWidth,
    minSize: isDocumentNarrow
      ? COVERAGE_PANE_NARROW_WIDTH
      : COVERAGE_DOCUMENT_MIN_WIDTH,
    maxSize: isDocumentNarrow
      ? COVERAGE_PANE_NARROW_WIDTH
      : COVERAGE_DOCUMENT_MAX_WIDTH,
  };

  // Remembering the width only while the pane is wide is what lets expanding restore the
  // size the user dragged to, instead of snapping back to the default half-and-half split.
  const onResizeEnd = ([, nextDocumentWidth]) => {
    if (!isDocumentNarrow) {
      setDocumentWidth(nextDocumentWidth);
    }
  };

  return (
    <SlidingPanel
      className={styles.trace_drawer}
      dataTestId="trace-drawer-panel"
    >
      <div
        aria-labelledby={TITLE_ID}
        className={styles.trace_drawer__dialog}
        data-testid="trace-drawer"
        ref={panelRef}
        role="dialog"
        tabIndex={-1}
      >
        <Flex className={styles.trace_drawer__header} gap={GAP_CLASS.GAP_200}>
          <Flex
            alignItems={ALIGN_CLASS["flex-start"]}
            className={styles.trace_drawer__headings}
            direction={DIRECTION_CLASS.column}
            gap={GAP_CLASS.GAP_050}
          >
            <span className={styles.trace_drawer__title} id={TITLE_ID}>
              Requirement Details
            </span>
            <Flex
              className={styles.trace_drawer__summary}
              gap={GAP_CLASS.GAP_100}
            >
              <span>Requirements traced to</span>

              <Ellipsis
                classes={styles.trace_drawer__summary__document_name}
                enableTooltip
                text={activeSection.title}
              />
            </Flex>
          </Flex>
          <span className={styles.trace_drawer__header_spacer} />
          <NewButton
            leftIcon={<ExternalLink size={14} aria-hidden />}
            onClick={() => onOpenInCoverage(sectionId)}
            size={NewButtonSize.sm}
            variant={NewButtonVariant.l2}
          >
            Open In Coverage
          </NewButton>

          <NewButton
            aria-label="Close The Trace Panel"
            leftIcon={<XIcon size={16} aria-hidden />}
            onClick={onClose}
            size={NewButtonSize.sm}
            variant={NewButtonVariant.l5}
          />
        </Flex>

        <Flex
          alignItems={ALIGN_CLASS.stretch}
          className={styles.trace_drawer__body}
        >
          <SplitVerticalPanels
            className={styles.trace_drawer__split}
            onResizeEnd={onResizeEnd}
            panels={[
              {
                initialSize: "1",
                size: "1",
                minSize: DRAWER_REQUIREMENTS_MIN_WIDTH,
                children: (
                  <Flex
                    alignItems={ALIGN_CLASS.stretch}
                    className={styles.trace_drawer__requirements}
                    direction={DIRECTION_CLASS.column}
                  >
                    <CoverageRequirementsTable
                      emptyMessage="No requirements linked to this section."
                      onSelectRow={(row) => setActiveRowId(row.id)}
                      rows={rows}
                      selectedRowIndex={activeRowIndex}
                      showAddressedIn={false}
                    />
                  </Flex>
                ),
              },
              {
                ...documentPanel,
                children: (
                  <TraceDocumentPane
                    activeRequirement={activeRow?.requirement}
                    documentId={documentId}
                    isDocumentNarrow={isDocumentNarrow}
                    onToggleNarrow={setIsDocumentNarrow}
                  />
                ),
              },
            ]}
          />
        </Flex>
      </div>
    </SlidingPanel>
  );
};

TraceDrawer.propTypes = {
  onClose: func.isRequired,
  onOpenInCoverage: func.isRequired,
  sectionId: oneOfType([number, string]).isRequired,
};

export default TraceDrawer;
